import { useState, useEffect, useRef, useCallback } from 'react';
import { T } from '../styles/theme';
import api, { API_BASE } from '../api';
import { useToast } from '../components/ToastContext';
import Layout from '../components/Layout';
import PhotoEditor from '../components/PhotoEditor';
import { useIsMobile } from '../hooks/useIsMobile';
import EbayConnect from '../components/EbayConnect';

// ─── Costanti ────────────────────────────────────────────────
const CATEGORIE = ['Scarpe', 'Abbigliamento', 'Profumi', 'Tech', 'Borse'];
const PIATTAFORME = ['Vinted', 'eBay'];
const TABS = [
  { key: 'tutte', label: 'Tutte' },
  { key: 'bozza', label: 'Bozze' },
  { key: 'modificata', label: 'Modificate' },
  { key: 'pubblicata', label: 'Pubblicate' },
];
const FRAMES = [
  { key: 'none', label: 'Nessuna' },
  { key: 'white', label: 'Bianca sottile' },
  { key: 'black', label: 'Nera elegante' },
  { key: 'violet', label: 'Viola gradiente' },
];
const WATERMARK_POS = [
  { key: 'bottomRight', label: 'Basso destra' },
  { key: 'bottomLeft', label: 'Basso sinistra' },
  { key: 'topRight', label: 'Alto destra' },
  { key: 'topLeft', label: 'Alto sinistra' },
  { key: 'center', label: 'Centro' },
];
const ACCOUNT_COLORS = ['#7c3aed', '#4ade80', '#fbbf24', '#f87171', '#22d3ee', '#ec4899'];

// ─── Componente Principale ───────────────────────────────────
export default function BozzePage() {
  const toast = useToast();
  const isMobile = useIsMobile();

  // Stato principale
  const [bozze, setBozze] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('tutte');

  // Modal
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorDraft, setEditorDraft] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishDraft, setPublishDraft] = useState(null);
  const [accountModalOpen, setAccountModalOpen] = useState(false);

  // ─── Fetch dati ──────────────────────────────────────────
  const fetchBozze = useCallback(async () => {
    try {
      const res = await api.get('/bozze');
      setBozze(Array.isArray(res.data) ? res.data : []);
    } catch {
      // silenzioso al primo caricamento
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await api.get('/account-piattaforme');
      setAccounts(res.data);
    } catch {
      /* silenzioso */
    }
  }, []);

  useEffect(() => {
    fetchBozze();
    fetchAccounts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Nuova bozza ────────────────────────────────────────
  const creaNuovaBozza = async () => {
    try {
      const res = await api.post('/bozze', {
        titolo: '',
        descrizione: '',
        prezzo: 0,
        categoria: 'Abbigliamento',
        hashtag: [],
        piattaforma: 'Vinted',
        foto_urls: [],
        note: '',
      });
      const nuova = res.data;
      toast.success('Nuova bozza creata');
      await fetchBozze();
      setEditorDraft(nuova);
      setEditorOpen(true);
    } catch {
      toast.error('Errore nella creazione della bozza');
    }
  };

  // ─── Elimina bozza ──────────────────────────────────────
  const eliminaBozza = async (id) => {
    try {
      await api.delete(`/bozze/${id}`);
      toast.success('Bozza eliminata');
      fetchBozze();
    } catch {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // ─── Filtro tab ──────────────────────────────────────────
  const bozzeFiltrate = tab === 'tutte'
    ? (bozze || [])
    : (bozze || []).filter((b) => b.stato === tab);

  // ─── Render ──────────────────────────────────────────────
  return (
    <Layout>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 24, paddingTop: 32 }}>
        <div>
          <h1 className="page-title" style={{ fontFamily: T.fontDisplay, margin: 0 }}>Le mie Bozze</h1>
          <p style={{ color: T.muted, fontSize: '0.85rem', fontFamily: T.fontBody, marginTop: 4 }}>
            Gestisci, modifica e pubblica i tuoi annunci
          </p>
        </div>
        <button className="btn-primary" onClick={creaNuovaBozza}
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: T.fontBody }}>
          + Nuova Bozza
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              padding: '6px 16px',
              borderRadius: 20,
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.82rem',
              fontWeight: 600,
              fontFamily: T.fontBody,
              background: tab === t.key ? T.violetDark : 'transparent',
              color: tab === t.key ? '#fff' : T.muted,
              transition: 'all 0.2s',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Griglia bozze */}
      {loading ? (
        <p style={{ color: T.muted, fontFamily: T.fontBody }}>Caricamento...</p>
      ) : bozzeFiltrate.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: T.muted, fontFamily: T.fontBody }}>
          <p style={{ fontSize: '1.2rem', marginBottom: 8 }}>Nessuna bozza trovata</p>
          <p style={{ fontSize: '0.85rem' }}>Crea la tua prima bozza per iniziare!</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: isMobile ? 8 : 16 }}>
          {bozzeFiltrate.map((b) => (
            <DraftCard
              key={b.id}
              bozza={b}
              isMobile={isMobile}
              onEdit={() => { setEditorDraft(b); setEditorOpen(true); }}
              onPublish={() => { setPublishDraft(b); setPublishOpen(true); }}
              onDelete={() => eliminaBozza(b.id)}
            />
          ))}
        </div>
      )}

      {/* Editor Modal */}
      {editorOpen && editorDraft && (
        <EditorModal
          draft={editorDraft}
          onClose={() => { setEditorOpen(false); setEditorDraft(null); }}
          onSaved={() => { fetchBozze(); setEditorOpen(false); setEditorDraft(null); }}
          onPublish={(d) => { setEditorOpen(false); setEditorDraft(null); setPublishDraft(d); setPublishOpen(true); }}
          toast={toast}
        />
      )}

      {/* Publish Modal */}
      {publishOpen && publishDraft && (
        <PublishModal
          draft={publishDraft}
          accounts={accounts}
          onClose={() => { setPublishOpen(false); setPublishDraft(null); }}
          onDone={() => { fetchBozze(); setPublishOpen(false); setPublishDraft(null); }}
          onManageAccounts={() => setAccountModalOpen(true)}
          toast={toast}
        />
      )}

      {/* Account Modal */}
      {accountModalOpen && (
        <AccountModal
          accounts={accounts}
          onClose={() => { setAccountModalOpen(false); fetchAccounts(); }}
          toast={toast}
          onRefresh={fetchAccounts}
        />
      )}
    </Layout>
  );
}

// ─── DraftCard ────────────────────────────────────────────────

function parseFotos(raw) {
  if (!raw) return [];
  if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return []; } }
  return Array.isArray(raw) ? raw : [];
}

function fotoSrc(url) {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_BASE}${url}`;
}

function getPhotoUrl(url) {
  if (!url) return null;
  if (typeof url !== 'string') return null;
  if (url.startsWith('data:')) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) return `${API_BASE}${url.replace('/uploads/', '/media/')}`;
  if (url.startsWith('uploads/')) return `${API_BASE}/media/${url.replace('uploads/', '')}`;
  if (url.startsWith('/media/')) return `${API_BASE}${url}`;
  if (url.startsWith('/')) return `${API_BASE}${url}`;
  return `${API_BASE}/media/${url}`;
}

function DraftCard({ bozza, isMobile, onEdit, onPublish, onDelete }) {
  const fotoOrig = parseFotos(bozza.foto_urls);
  const fotoMod = parseFotos(bozza.foto_modificate_urls);
  // Merge: usa la versione modificata se esiste, altrimenti l'originale
  const fotos = fotoOrig.map((orig, i) => fotoMod[i] || orig).filter(Boolean);
  const foto = getPhotoUrl(fotos[0]);
  const data = bozza.data_creazione ? new Date(bozza.data_creazione).toLocaleDateString('it-IT') : (bozza.created_at ? new Date(bozza.created_at).toLocaleDateString('it-IT') : '');

  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm('Eliminare questa bozza?')) onDelete();
  };

  return (
    <div style={{
      background: T.card,
      border: `1px solid ${T.border}`,
      borderRadius: 20,
      overflow: 'hidden',
      transition: 'border-color 0.2s, transform 0.2s',
      position: 'relative',
    }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.borderActive; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      {/* Delete button */}
      <button onClick={handleDelete} style={{
        position: 'absolute', top: 12, right: 12, zIndex: 10,
        width: isMobile ? 24 : 32, height: isMobile ? 24 : 32, borderRadius: '50%',
        background: 'rgba(248,113,113,0.15)', border: '1px solid #f87171',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', transition: '0.15s', fontSize: '0.9rem',
      }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(248,113,113,0.3)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(248,113,113,0.15)'}>
        🗑️
      </button>

      {/* Foto */}
      <div style={{ position: 'relative', aspectRatio: isMobile ? '1/1' : '4/3', background: T.elem, overflow: 'hidden' }}>
        {foto ? (
          <img src={foto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.target.style.display = 'none'; }} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: T.elem, color: T.muted, fontSize: '2rem' }}>
            📝
          </div>
        )}
        {bozza.modificata_con_editor && (
          <span style={{
            position: 'absolute', top: 8, left: 8,
            background: T.gradPrimary, color: '#fff',
            fontSize: '0.65rem', fontWeight: 700,
            padding: '3px 8px', borderRadius: 8,
            fontFamily: T.fontBody,
          }}>
            ✏️ Modificata
          </span>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: isMobile ? '8px' : '14px 16px 10px' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
          {bozza.piattaforma && (
            <span className="badge badge-violet" style={{ fontSize: isMobile ? '0.58rem' : '0.65rem', padding: isMobile ? '2px 6px' : undefined }}>{bozza.piattaforma}</span>
          )}
          {bozza.stato && bozza.stato !== 'bozza' && (
            <span className={`badge ${bozza.stato === 'pubblicata' ? 'badge-green' : 'badge-amber'}`} style={{ fontSize: '0.65rem' }}>
              {bozza.stato}
            </span>
          )}
        </div>
        <p className="line-clamp-2" style={{
          fontSize: isMobile ? '0.72rem' : '0.95rem', fontWeight: 700, color: T.text,
          fontFamily: T.fontBody, margin: '0 0 6px', minHeight: isMobile ? 30 : 40, lineHeight: isMobile ? 1.2 : undefined,
          overflow: 'hidden',
        }}>
          {bozza.titolo || 'Bozza senza titolo'}
        </p>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: isMobile ? '0.82rem' : '1rem', fontWeight: 800, color: T.violet, fontFamily: T.fontBody }}>
            {bozza.prezzo ? `€${Number(bozza.prezzo).toFixed(2)}` : '—'}
          </span>
          <span style={{ fontSize: isMobile ? '0.6rem' : '0.72rem', color: T.muted, fontFamily: T.fontBody }}>{data}</span>
        </div>
      </div>

      {/* Footer with proper button styling */}
      <div style={{ display: 'flex', gap: isMobile ? 4 : 8, padding: isMobile ? '0 8px 10px' : '0 16px 14px' }}>
        <button onClick={onEdit} style={{
          flex: 1, textAlign: 'center', padding: isMobile ? '6px 0' : '10px 0', fontFamily: T.fontBody, fontWeight: 600, fontSize: isMobile ? '0.65rem' : '0.85rem',
          background: T.elem, border: `1px solid ${T.border}`, borderRadius: 24, color: T.text,
          cursor: 'pointer', transition: '0.15s',
        }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = T.violet}
          onMouseLeave={(e) => e.currentTarget.style.borderColor = T.border}
          onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.97)'}
          onMouseUp={(e) => e.currentTarget.style.transform = ''}>
          ✏️ Modifica
        </button>
        <button onClick={onPublish} style={{
          flex: 1, textAlign: 'center', padding: isMobile ? '6px 0' : '10px 0', fontFamily: T.fontBody, fontWeight: 600, fontSize: isMobile ? '0.65rem' : '0.85rem',
          background: T.gradPrimary, border: 'none', borderRadius: 24, color: 'white',
          cursor: 'pointer', transition: '0.15s',
        }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(124,58,237,0.4)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.boxShadow = 'none'; }}
          onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.97)'}
          onMouseUp={(e) => e.currentTarget.style.transform = ''}>
          🚀 Pubblica
        </button>
      </div>
    </div>
  );
}

// ─── EditorModal ──────────────────────────────────────────────
function EditorModal({ draft, onClose, onSaved, onPublish, toast }) {
  const isMobile = useIsMobile();
  const [titolo, setTitolo] = useState(draft.titolo || '');
  const [descrizione, setDescrizione] = useState(draft.descrizione || '');
  const [prezzo, setPrezzo] = useState(draft.prezzo || '');
  const [categoria, setCategoria] = useState(draft.categoria || 'Abbigliamento');
  const [hashtag, setHashtag] = useState(draft.hashtag || []);
  const [hashInput, setHashInput] = useState('');
  const [piattaforma, setPiattaforma] = useState(draft.piattaforma || 'Vinted');
  const [fotoUrls, setFotoUrls] = useState(draft.foto_urls || []);
  const [fotoModUrls, setFotoModUrls] = useState(draft.foto_modificate_urls || []);
  const [note, setNote] = useState(draft.note || '');
  const [saving, setSaving] = useState(false);

  // Drag & drop foto
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const touchStartRef = useRef(null);

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };
  const handleDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) { handleDragEnd(); return; }
    const newUrls = [...fotoUrls];
    const newMod = [...fotoModUrls];
    const [movedUrl] = newUrls.splice(dragIdx, 1);
    newUrls.splice(idx, 0, movedUrl);
    const [movedMod] = newMod.splice(dragIdx, 1);
    newMod.splice(idx, 0, movedMod);
    setFotoUrls(newUrls);
    setFotoModUrls(newMod);
    handleDragEnd();
  };

  // Touch drag per mobile
  const handleTouchStart = (e, idx) => {
    touchStartRef.current = { idx, y: e.touches[0].clientY, x: e.touches[0].clientX };
    setDragIdx(idx);
  };
  const handleTouchMove = (e, idx) => {
    if (touchStartRef.current === null) return;
    const touch = e.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    if (el) {
      const dropTarget = el.closest('[data-photo-idx]');
      if (dropTarget) {
        const overIdx = parseInt(dropTarget.dataset.photoIdx);
        if (!isNaN(overIdx)) setDragOverIdx(overIdx);
      }
    }
  };
  const handleTouchEnd = () => {
    if (dragIdx !== null && dragOverIdx !== null && dragIdx !== dragOverIdx) {
      handleDrop(dragOverIdx);
    }
    touchStartRef.current = null;
    handleDragEnd();
  };

  // Photo editor state
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(0);
  const [saturation, setSaturation] = useState(0);
  const [frame, setFrame] = useState('none');
  const [watermarkOn, setWatermarkOn] = useState(false);
  const [watermarkText, setWatermarkText] = useState('');
  const [watermarkPos, setWatermarkPos] = useState('bottomRight');
  const [watermarkOpacity, setWatermarkOpacity] = useState(70);

  const canvasRef = useRef(null);
  const imgRef = useRef(null);

  // Disegna canvas
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !img.complete) return;

    const ctx = canvas.getContext('2d');
    const size = 300;
    canvas.width = size;
    canvas.height = size;

    ctx.clearRect(0, 0, size, size);
    ctx.save();

    // Filtri
    const b = 100 + brightness;
    const c = 100 + contrast;
    const s = 100 + saturation;
    ctx.filter = `brightness(${b}%) contrast(${c}%) saturate(${s}%)`;

    // Trasformazioni
    ctx.translate(size / 2, size / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    if (flipH) ctx.scale(-1, 1);

    const scale = Math.min(size / img.naturalWidth, size / img.naturalHeight);
    const w = img.naturalWidth * scale;
    const h = img.naturalHeight * scale;
    ctx.drawImage(img, -w / 2, -h / 2, w, h);

    ctx.restore();

    // Frame
    if (frame === 'white') {
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 6;
      ctx.strokeRect(3, 3, size - 6, size - 6);
    } else if (frame === 'black') {
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 8;
      ctx.strokeRect(4, 4, size - 8, size - 8);
    } else if (frame === 'violet') {
      const grad = ctx.createLinearGradient(0, 0, size, size);
      grad.addColorStop(0, '#7c3aed');
      grad.addColorStop(1, '#4f46e5');
      ctx.strokeStyle = grad;
      ctx.lineWidth = 8;
      ctx.strokeRect(4, 4, size - 8, size - 8);
    }

    // Watermark
    if (watermarkOn && watermarkText) {
      ctx.save();
      ctx.globalAlpha = watermarkOpacity / 100;
      ctx.fillStyle = '#ffffff';
      ctx.font = '16px DM Sans, sans-serif';
      ctx.textBaseline = 'bottom';
      const tw = ctx.measureText(watermarkText).width;
      let x, y;
      switch (watermarkPos) {
        case 'bottomLeft': x = 12; y = size - 12; break;
        case 'topRight': x = size - tw - 12; y = 24; ctx.textBaseline = 'top'; break;
        case 'topLeft': x = 12; y = 24; ctx.textBaseline = 'top'; break;
        case 'center': x = (size - tw) / 2; y = size / 2; ctx.textBaseline = 'middle'; break;
        default: x = size - tw - 12; y = size - 12; break;
      }
      ctx.fillText(watermarkText, x, y);
      ctx.restore();
    }
  }, [rotation, flipH, brightness, contrast, saturation, frame, watermarkOn, watermarkText, watermarkPos, watermarkOpacity]);

  useEffect(() => {
    if (selectedPhoto) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        imgRef.current = img;
        drawCanvas();
      };
      img.onerror = () => { console.warn('Foto non caricata:', selectedPhoto); };
      img.src = getPhotoUrl(selectedPhoto);
    }
  }, [selectedPhoto, drawCanvas]);

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) drawCanvas();
  }, [drawCanvas]);

  const resetEditor = () => {
    setRotation(0);
    setFlipH(false);
    setBrightness(0);
    setContrast(0);
    setSaturation(0);
    setFrame('none');
    setWatermarkOn(false);
    setWatermarkText('');
    setWatermarkPos('bottomRight');
    setWatermarkOpacity(70);
  };

  const applyEdit = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const idx = fotoUrls.indexOf(selectedPhoto);
    if (idx >= 0) {
      const newMod = [...fotoModUrls];
      newMod[idx] = dataUrl;
      setFotoModUrls(newMod);
      toast.success('Modifica applicata alla foto');
    }
  };

  const addHashtag = (e) => {
    if (e.key === 'Enter' && hashInput.trim()) {
      e.preventDefault();
      const tag = hashInput.trim().startsWith('#') ? hashInput.trim() : `#${hashInput.trim()}`;
      if (!hashtag.includes(tag)) {
        setHashtag([...hashtag, tag]);
      }
      setHashInput('');
    }
  };

  const removeHashtag = (idx) => {
    setHashtag(hashtag.filter((_, i) => i !== idx));
  };

  // Aggiungi foto da file
  const fileInputRef = useRef(null);
  const handleAddPhoto = () => {
    fileInputRef.current?.click();
  };
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setFotoUrls((prev) => [...prev, ev.target.result]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removePhoto = (idx) => {
    setFotoUrls(fotoUrls.filter((_, i) => i !== idx));
    const newMod = [...fotoModUrls];
    newMod.splice(idx, 1);
    setFotoModUrls(newMod);
    if (selectedPhoto === fotoUrls[idx]) setSelectedPhoto(null);
  };

  // Salva bozza
  const salvaBozza = async () => {
    setSaving(true);
    try {
      await api.put(`/bozze/${draft.id}`, {
        titolo,
        descrizione,
        prezzo: parseFloat(prezzo) || 0,
        categoria,
        hashtag,
        piattaforma,
        foto_urls: fotoUrls,
        foto_modificate_urls: fotoModUrls,
        modificata_con_editor: fotoModUrls.some(Boolean),
        note,
        stato: 'bozza',
      });
      toast.success('Bozza salvata!');
      onSaved();
    } catch {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const pubblicaOra = async () => {
    setSaving(true);
    try {
      await api.put(`/bozze/${draft.id}`, {
        titolo,
        descrizione,
        prezzo: parseFloat(prezzo) || 0,
        categoria,
        hashtag,
        piattaforma,
        foto_urls: fotoUrls,
        foto_modificate_urls: fotoModUrls,
        modificata_con_editor: fotoModUrls.some(Boolean),
        note,
        stato: 'bozza',
      });
      const updated = {
        ...draft,
        titolo, descrizione, prezzo: parseFloat(prezzo) || 0,
        categoria, hashtag, piattaforma, foto_urls: fotoUrls,
        foto_modificate_urls: fotoModUrls,
      };
      onPublish(updated);
    } catch {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // ─── Stili inline comuni ─────────
  const inputStyle = {
    width: '100%',
    background: T.elem,
    border: `1px solid ${T.border}`,
    borderRadius: 12,
    padding: '10px 14px',
    color: T.text,
    fontSize: '0.85rem',
    fontFamily: T.fontBody,
    outline: 'none',
    boxSizing: 'border-box',
  };
  const labelStyle = {
    display: 'block',
    color: T.muted,
    fontSize: '0.75rem',
    fontWeight: 600,
    marginBottom: 6,
    fontFamily: T.fontBody,
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(13,11,30,0.98)',
      zIndex: 200,
      overflow: 'auto',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: isMobile ? '16px' : '20px 32px',
        borderBottom: `1px solid ${T.border}`,
      }}>
        <h2 style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: isMobile ? '1rem' : '1.3rem', margin: 0 }}>
          Editor Annuncio
        </h2>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', color: T.muted, fontSize: '1.5rem', cursor: 'pointer',
        }}>
          ✕
        </button>
      </div>

      {/* Content — responsive */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1.5fr 1fr',
        gap: isMobile ? 16 : 32,
        padding: isMobile ? '16px' : '24px 32px',
        maxWidth: 1200,
        margin: '0 auto',
      }}>
        {/* LEFT - Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Titolo */}
          <div>
            <label style={labelStyle}>Titolo</label>
            <div style={{ position: 'relative' }}>
              <input style={inputStyle} value={titolo}
                onChange={(e) => setTitolo(e.target.value.slice(0, 60))}
                placeholder="Titolo dell'annuncio" maxLength={60} />
              <span style={{
                position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                fontSize: '0.7rem', color: T.muted, fontFamily: T.fontMono,
              }}>
                {titolo.length}/60
              </span>
            </div>
          </div>

          {/* Descrizione */}
          <div>
            <label style={labelStyle}>Descrizione</label>
            <div style={{ position: 'relative' }}>
              <textarea style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }}
                value={descrizione}
                onChange={(e) => setDescrizione(e.target.value.slice(0, 300))}
                placeholder="Descrivi il tuo prodotto..." maxLength={300} />
              <span style={{
                position: 'absolute', right: 12, bottom: 10,
                fontSize: '0.7rem', color: T.muted, fontFamily: T.fontMono,
              }}>
                {descrizione.length}/300
              </span>
            </div>
          </div>

          {/* Prezzo + Categoria */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={labelStyle}>Prezzo (EUR)</label>
              <input style={inputStyle} type="number" step="0.01" min="0"
                value={prezzo} onChange={(e) => setPrezzo(e.target.value)}
                placeholder="0.00" />
            </div>
            <div>
              <label style={labelStyle}>Categoria</label>
              <select style={{ ...inputStyle, cursor: 'pointer' }}
                value={categoria} onChange={(e) => setCategoria(e.target.value)}>
                {CATEGORIE.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Hashtag */}
          <div>
            <label style={labelStyle}>Hashtag (premi Invio per aggiungere)</label>
            <input style={inputStyle} value={hashInput}
              onChange={(e) => setHashInput(e.target.value)}
              onKeyDown={addHashtag}
              placeholder="#hashtag" />
            {hashtag.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {hashtag.map((h, i) => (
                  <span key={i} style={{
                    background: T.elem, border: `1px solid ${T.border}`,
                    borderRadius: 12, padding: '4px 10px',
                    fontSize: '0.75rem', color: T.violet,
                    fontFamily: T.fontBody,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    {h}
                    <span onClick={() => removeHashtag(i)} style={{
                      cursor: 'pointer', color: T.muted, fontWeight: 700, fontSize: '0.8rem',
                    }}>
                      ×
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Piattaforma */}
          <div>
            <label style={labelStyle}>Piattaforma</label>
            <div style={{ display: 'flex', gap: 10 }}>
              {PIATTAFORME.map((p) => (
                <button key={p} onClick={() => setPiattaforma(p)} style={{
                  flex: 1,
                  padding: '10px 0',
                  borderRadius: 12,
                  border: `1px solid ${piattaforma === p ? T.violetDark : T.border}`,
                  background: piattaforma === p ? T.violetDark : T.elem,
                  color: piattaforma === p ? '#fff' : T.muted,
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  fontFamily: T.fontBody,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}>
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Note */}
          <div>
            <label style={labelStyle}>Note (opzionale)</label>
            <input style={inputStyle} value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note interne..." />
          </div>
        </div>

        {/* RIGHT - Photo Editor */}
        <div style={{
          background: T.card,
          border: `1px solid ${T.border}`,
          borderRadius: 20,
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: T.text, fontWeight: 700, fontSize: '0.9rem', fontFamily: T.fontBody }}>
              Foto
            </span>
            <button onClick={handleAddPhoto} style={{
              background: T.elem, border: `1px solid ${T.border}`, borderRadius: 10,
              padding: '5px 12px', color: T.violet, fontSize: '0.75rem', fontWeight: 600,
              cursor: 'pointer', fontFamily: T.fontBody,
            }}>
              + Aggiungi
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" multiple
              onChange={handleFileChange} style={{ display: 'none' }} />
          </div>

          {/* Thumbnails — drag & drop per riordinare */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {fotoUrls.map((url, i) => (
              <div key={`${i}-${url?.slice?.(-10)}`} data-photo-idx={i}
                draggable
                onDragStart={() => handleDragStart(i)}
                onDragOver={(e) => handleDragOver(e, i)}
                onDrop={() => handleDrop(i)}
                onDragEnd={handleDragEnd}
                onTouchStart={(e) => handleTouchStart(e, i)}
                onTouchMove={(e) => handleTouchMove(e, i)}
                onTouchEnd={handleTouchEnd}
                style={{
                  position: 'relative',
                  opacity: dragIdx === i ? 0.4 : 1,
                  transform: dragOverIdx === i && dragIdx !== i ? 'scale(1.05)' : '',
                  transition: 'opacity 0.15s, transform 0.15s',
                }}>
                {/* Numero posizione */}
                <span style={{
                  position: 'absolute', top: 4, left: 4, zIndex: 3,
                  width: 20, height: 20, borderRadius: '50%',
                  background: i === 0 ? T.violetDark : 'rgba(13,11,30,0.7)',
                  color: '#fff', fontSize: '0.6rem', fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: `1px solid ${i === 0 ? T.violet : 'rgba(255,255,255,0.2)'}`,
                  pointerEvents: 'none',
                }}>{i + 1}</span>
                <div
                  onClick={() => { setSelectedPhoto(url); resetEditor(); }}
                  style={{
                    width: '100%',
                    height: isMobile ? 80 : 100,
                    borderRadius: 12,
                    overflow: 'hidden',
                    border: `2px solid ${dragOverIdx === i && dragIdx !== i ? T.violet : selectedPhoto === url ? T.violetDark : T.border}`,
                    cursor: 'grab',
                    transition: 'border-color 0.2s',
                  }}>
                  <img src={getPhotoUrl(fotoModUrls[i] || url)} alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
                    onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
                <button onClick={(e) => { e.stopPropagation(); removePhoto(i); }} style={{
                  position: 'absolute', top: -4, right: -4, zIndex: 3,
                  background: T.red, border: 'none', borderRadius: '50%',
                  width: 20, height: 20, color: '#fff', fontSize: '0.65rem',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700,
                }}>
                  ×
                </button>
              </div>
            ))}
          </div>
          {fotoUrls.length > 1 && (
            <p style={{ fontSize: '0.68rem', color: T.muted, textAlign: 'center', margin: 0 }}>
              Trascina le foto per riordinarle
            </p>
          )}

          {/* Canvas editor */}
          {selectedPhoto && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <canvas ref={canvasRef}
                style={{
                  width: '100%', maxWidth: 300, aspectRatio: '1', borderRadius: 12,
                  border: `1px solid ${T.border}`, margin: '0 auto',
                }} />

              {/* Rotazione e Flip */}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <MiniBtn label="↶ 90°" onClick={() => setRotation((r) => r - 90)} />
                <MiniBtn label="↷ 90°" onClick={() => setRotation((r) => r + 90)} />
                <MiniBtn label="↔ Flip" onClick={() => setFlipH(!flipH)} />
              </div>

              {/* Sliders */}
              <SliderRow label="Luminosita" value={brightness} onChange={setBrightness} />
              <SliderRow label="Contrasto" value={contrast} onChange={setContrast} />
              <SliderRow label="Saturazione" value={saturation} onChange={setSaturation} />

              {/* Frame */}
              <div>
                <span style={{ color: T.muted, fontSize: '0.72rem', fontFamily: T.fontBody }}>Cornice</span>
                <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                  {FRAMES.map((f) => (
                    <button key={f.key} onClick={() => setFrame(f.key)} style={{
                      padding: '4px 10px', borderRadius: 8,
                      border: `1px solid ${frame === f.key ? T.violetDark : T.border}`,
                      background: frame === f.key ? T.violetDark : T.elem,
                      color: frame === f.key ? '#fff' : T.muted,
                      fontSize: '0.7rem', cursor: 'pointer', fontFamily: T.fontBody,
                    }}>
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Watermark */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: T.muted, fontSize: '0.72rem', fontFamily: T.fontBody }}>Watermark</span>
                  <label style={{ position: 'relative', display: 'inline-block', width: 36, height: 20, cursor: 'pointer' }}>
                    <input type="checkbox" checked={watermarkOn} onChange={(e) => setWatermarkOn(e.target.checked)}
                      style={{ opacity: 0, width: 0, height: 0 }} />
                    <span style={{
                      position: 'absolute', inset: 0, borderRadius: 10,
                      background: watermarkOn ? T.violetDark : T.faint,
                      transition: 'background 0.2s',
                    }} />
                    <span style={{
                      position: 'absolute', top: 2, left: watermarkOn ? 18 : 2,
                      width: 16, height: 16, borderRadius: '50%',
                      background: '#fff', transition: 'left 0.2s',
                    }} />
                  </label>
                </div>
                {watermarkOn && (
                  <>
                    <input style={{ ...inputStyle, fontSize: '0.78rem', padding: '6px 10px' }}
                      value={watermarkText} onChange={(e) => setWatermarkText(e.target.value)}
                      placeholder="Testo watermark" />
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <select style={{ ...inputStyle, flex: 1, fontSize: '0.75rem', padding: '6px 8px' }}
                        value={watermarkPos} onChange={(e) => setWatermarkPos(e.target.value)}>
                        {WATERMARK_POS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                      </select>
                      <div style={{ flex: 1 }}>
                        <SliderRow label="Opacita" value={watermarkOpacity} onChange={setWatermarkOpacity} min={10} max={100} />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Azioni editor */}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-ghost" onClick={resetEditor}
                  style={{ flex: 1, fontSize: '0.78rem', fontFamily: T.fontBody }}>
                  Reset
                </button>
                <button className="btn-primary" onClick={applyEdit}
                  style={{ flex: 1, fontSize: '0.78rem', fontFamily: T.fontBody }}>
                  Applica
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{
        display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end', gap: isMobile ? 8 : 12,
        padding: isMobile ? '12px 16px' : '16px 32px',
        borderTop: `1px solid ${T.border}`,
        maxWidth: 1200,
        margin: '0 auto',
        flexWrap: 'wrap',
      }}>
        <button className="btn-ghost" onClick={onClose}
          style={{ fontFamily: T.fontBody, fontSize: isMobile ? '0.8rem' : undefined }}>
          Annulla
        </button>
        <button className="btn-secondary" onClick={salvaBozza} disabled={saving}
          style={{ fontFamily: T.fontBody, fontSize: isMobile ? '0.8rem' : undefined }}>
          {saving ? 'Salvataggio...' : 'Salva Bozza'}
        </button>
        <button className="btn-primary" onClick={pubblicaOra} disabled={saving}
          style={{ fontFamily: T.fontBody, fontSize: isMobile ? '0.8rem' : undefined }}>
          Pubblica Ora
        </button>
      </div>
    </div>
  );
}

// ─── PublishModal ─────────────────────────────────────────────
function PublishModal({ draft, accounts, onClose, onDone, onManageAccounts, toast }) {
  const [piattaforma, setPiattaforma] = useState(draft.piattaforma || 'Vinted');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [fotoModificate, setFotoModificate] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showVintedInstructions, setShowVintedInstructions] = useState(false);
  const [vintedReadyUrl, setVintedReadyUrl] = useState('');
  const [showVintedLink, setShowVintedLink] = useState(false);

  const platformAccounts = accounts.filter((a) => a.piattaforma === piattaforma);
  const selAccount = platformAccounts.find(a => a.id === selectedAccount);
  const fotoOrig = parseFotos(draft.foto_urls);
  const fotoMod = parseFotos(draft.foto_modificate_urls);
  const fotoArr = fotoOrig.map((orig, i) => fotoMod[i] || orig).filter(Boolean);
  const foto = getPhotoUrl(fotoArr[0]);

  const pubblica = async () => {
    if (piattaforma === 'Vinted') {
      // Apri la tab SUBITO (sincrono, dentro il click handler) per evitare il popup blocker di Chrome
      const newTab = window.open('about:blank', '_blank');
      if (!newTab) {
        toast.error("Chrome ha bloccato l'apertura della tab. Autorizza i popup per dashboard.proelite.it nelle impostazioni del browser.");
        return;
      }
      try {
        newTab.document.write('<html><head><title>Apro Vinted...</title></head><body style="font-family:system-ui;background:#0c0a1a;color:#a78bfa;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;"><div style="text-align:center;"><h2>Preparazione dati...</h2><p>Tra qualche secondo verrai reindirizzato a Vinted</p></div></body></html>');
      } catch (e) { /* ignora se about:blank non permette document.write */ }

      try {
        // Comprimi foto in base64 (max 800px, JPEG 0.75)
        const fotoOriginali = fotoArr.map(getPhotoUrl).filter(Boolean);
        const haModificate = fotoModificate.some(Boolean);
        const fotoFinali = haModificate
          ? fotoOriginali.map((orig, i) => fotoModificate[i] || orig)
          : fotoOriginali;

        let fotoCompresse = [];
        for (const url of fotoFinali.slice(0, 20)) {
          try {
            let blob;
            if (url.startsWith('data:')) {
              const r = await fetch(url); blob = await r.blob();
            } else {
              let path = url;
              if (url.startsWith('http')) { try { path = new URL(url).pathname; } catch {} }
              else if (!url.startsWith('/')) { path = '/media/' + url; }
              const proxyUrl = `${API_BASE}/proxy-image?url=${encodeURIComponent(path)}`;
              const r = await fetch(proxyUrl); blob = await r.blob();
            }
            const b64 = await new Promise(resolve => {
              const img = new Image();
              img.onload = () => {
                const c = document.createElement('canvas');
                const max = 800;
                let w = img.width, h = img.height;
                if (w > h) { h = Math.round(h * max / w); w = max; } else { w = Math.round(w * max / h); h = max; }
                c.width = w; c.height = h;
                c.getContext('2d').drawImage(img, 0, 0, w, h);
                resolve(c.toDataURL('image/jpeg', 0.75));
              };
              img.onerror = () => resolve(null);
              img.src = URL.createObjectURL(blob);
            });
            if (b64) fotoCompresse.push(b64);
          } catch (e) { console.warn('[Foto] Errore:', e); }
        }

        const vintedData = {
          titolo: draft.titolo || '',
          descrizione: (draft.descrizione || '').replace(/<[^>]*>/g, '').replace(/&[^;]+;/g, ' ').trim(),
          prezzo: parseFloat(draft.prezzo) || 0,
          categoria: draft.categoria || '',
          marca: draft.marca || '',
          taglia: draft.taglia || '',
          colore: draft.colore || '',
          condizioni: draft.condizione || 'Buone',
          fotoBase64: fotoCompresse,
          accountSelezionato: selAccount?.username || '',
        };

        // Salva i dati nel backend e passa solo l'ID nell'hash
        const saveRes = await fetch(`${API_BASE}/draft/save`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(vintedData),
        });
        if (!saveRes.ok) throw new Error('HTTP ' + saveRes.status);
        const { draft_id } = await saveRes.json();
        const vintedUrl = `https://www.vinted.it/items/new#IE_DRAFT=${draft_id}`;
        console.log('[Bozze] Apro Vinted, draft_id:', draft_id, 'foto:', fotoCompresse.length);
        if (newTab && !newTab.closed) {
          newTab.location.href = vintedUrl;
          toast.success('Apro Vinted con i dati...');
          setShowVintedInstructions(true);
        } else {
          toast.error('La tab è stata chiusa prima di completare la preparazione');
        }
      } catch (err) {
        if (newTab && !newTab.closed) newTab.close();
        toast.error('Errore preparazione dati');
        console.error('[Bozze] Errore:', err);
      }
      return;
    }

    if (piattaforma === 'eBay') {
      setPublishing(true);
      try {
        const res = await api.post(`/bozze/${draft.id}/pubblica-ebay`);
        if (res.data?.link) {
          toast.success(
            <span>Pubblicato su eBay! <a href={res.data.link} target="_blank" rel="noopener noreferrer" style={{ color: '#a78bfa', textDecoration: 'underline' }}>Vedi annuncio</a></span>
          );
        } else {
          toast.success('Annuncio pubblicato su eBay!');
        }
        onDone();
      } catch (err) {
        const detail = err.response?.data?.detail || 'Errore nella pubblicazione su eBay';
        if (err.response?.status === 401) {
          toast.error('Collega prima il tuo account eBay dal Profilo');
        } else {
          toast.error(detail);
        }
      } finally {
        setPublishing(false);
      }
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(13,11,30,0.85)',
      zIndex: 250,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: '#151230',
        border: '1px solid #2d1f6e',
        borderRadius: 24,
        padding: 28,
        maxWidth: 560,
        width: '92%',
        maxHeight: '90vh',
        overflow: 'auto',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: '1.15rem', margin: 0 }}>
            Pubblica Annuncio
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.muted, fontSize: '1.3rem', cursor: 'pointer' }}>✕</button>
        </div>

        {/* SEZIONE 1 — Piattaforma */}
        <p style={{ color: '#6b7280', fontSize: '0.75rem', fontFamily: T.fontBody, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Piattaforma
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
          {PIATTAFORME.map((p) => (
            <button key={p} onClick={() => { setPiattaforma(p); setSelectedAccount(null); }} style={{
              padding: '16px 0', borderRadius: 16,
              border: `2px solid ${piattaforma === p ? '#7c3aed' : '#2d1f6e'}`,
              background: piattaforma === p ? 'rgba(124,58,237,0.08)' : 'transparent',
              color: piattaforma === p ? T.text : T.muted,
              fontWeight: 700, fontSize: '1rem', fontFamily: T.fontBody,
              cursor: 'pointer', transition: 'all 0.2s',
            }}>
              {p === 'Vinted' ? '👗 Vinted' : '🏷️ eBay'}
            </button>
          ))}
        </div>
        {piattaforma === 'eBay' && (
          <div style={{ marginBottom: 16 }}>
            <EbayConnect compact />
          </div>
        )}

        {/* SEZIONE 2 — Account dropdown custom */}
        <p style={{ color: '#6b7280', fontSize: '0.75rem', fontFamily: T.fontBody, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Account
        </p>
        <div style={{ position: 'relative', marginBottom: 12 }}>
          {/* Trigger dropdown */}
          <div onClick={() => setDropdownOpen(!dropdownOpen)} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: '#1c1640', border: `1px solid ${selectedAccount ? '#7c3aed' : '#2d1b69'}`,
            borderRadius: 14, padding: '12px 16px', cursor: 'pointer',
          }}>
            {selAccount ? (
              <>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: selAccount.colore || '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.75rem', fontWeight: 700, fontFamily: T.fontBody, flexShrink: 0 }}>
                  {(selAccount.nome_account || '?')[0].toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{ color: T.text, fontSize: '0.85rem', fontWeight: 600, fontFamily: T.fontBody }}>{selAccount.nome_account}</span>
                  {selAccount.username && <span style={{ color: T.muted, fontSize: '0.72rem', marginLeft: 8, fontFamily: T.fontBody }}>@{selAccount.username}</span>}
                </div>
              </>
            ) : (
              <span style={{ color: T.muted, fontSize: '0.85rem', fontFamily: T.fontBody, flex: 1 }}>Seleziona account {piattaforma}...</span>
            )}
            <span style={{ color: '#a78bfa', fontSize: '0.7rem', transition: 'transform 0.2s', transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0)', flexShrink: 0 }}>▼</span>
          </div>

          {/* Lista dropdown */}
          {dropdownOpen && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
              background: '#151230', border: '1px solid #2d1b69', borderRadius: 14,
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 100,
              maxHeight: 200, overflowY: 'auto',
            }}>
              {platformAccounts.map((a) => (
                <div key={a.id} onClick={() => { setSelectedAccount(a.id); setDropdownOpen(false); }} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', cursor: 'pointer',
                  background: selectedAccount === a.id ? '#1c1640' : 'transparent',
                  transition: 'background 0.15s',
                }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: a.colore || '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.75rem', fontWeight: 700, fontFamily: T.fontBody, flexShrink: 0 }}>
                    {(a.nome_account || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{ color: T.text, fontSize: '0.85rem', fontWeight: 600, fontFamily: T.fontBody }}>{a.nome_account}</span>
                    {a.username && <span style={{ color: T.muted, fontSize: '0.72rem', marginLeft: 8, fontFamily: T.fontBody }}>@{a.username}</span>}
                  </div>
                  {selectedAccount === a.id && <span style={{ color: '#7c3aed', fontSize: '1rem' }}>✓</span>}
                </div>
              ))}
              <div onClick={() => { setDropdownOpen(false); onManageAccounts(); }} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 14px', cursor: 'pointer',
                borderTop: platformAccounts.length > 0 ? '1px solid #2d1b69' : 'none',
              }}>
                <span style={{ color: '#a78bfa', fontSize: '0.85rem', fontWeight: 600, fontFamily: T.fontBody }}>+ Aggiungi account</span>
              </div>
            </div>
          )}
        </div>

        {/* Info box account */}
        <div style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid #2d1b69', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
          <p style={{ color: '#6b7280', fontSize: '0.8rem', fontFamily: T.fontBody, margin: 0 }}>
            💡 Assicurati di essere loggato con questo account su {piattaforma} prima di pubblicare
          </p>
        </div>

        {/* SEZIONE 3 — Editor foto (collassabile) */}
        <PhotoEditor
          fotoUrls={fotoArr}
          onFotoModificate={(edited) => setFotoModificate(edited)}
        />

        {/* SEZIONE 4 — Anteprima (collassabile) */}
        <div style={{ marginTop: 12 }}>
          <div onClick={() => setShowPreview(!showPreview)} style={{
            display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', padding: '8px 0',
          }}>
            <span style={{ fontFamily: T.fontBody, fontSize: '0.85rem', fontWeight: 600, color: '#a78bfa' }}>👁 Anteprima</span>
            <span style={{ color: '#a78bfa', fontSize: '0.7rem', transition: 'transform 0.2s', transform: showPreview ? 'rotate(180deg)' : 'rotate(0)' }}>▼</span>
          </div>
          {showPreview && (
            <div style={{
              background: '#1c1640', border: '1px solid #2d1b69', borderRadius: 16,
              padding: 14, display: 'flex', gap: 12, alignItems: 'center',
            }}>
              {foto ? (
                <img src={foto} alt="" style={{ width: 60, height: 60, borderRadius: 10, objectFit: 'cover' }}
                  onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <div style={{ width: 60, height: 60, borderRadius: 10, background: '#151230', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.muted }}>📷</div>
              )}
              <div>
                <p style={{ color: T.text, fontSize: '0.85rem', fontWeight: 600, fontFamily: T.fontBody, margin: 0 }}>{draft.titolo || 'Senza titolo'}</p>
                <p style={{ color: '#7c3aed', fontSize: '0.9rem', fontWeight: 800, fontFamily: T.fontMono, margin: '4px 0 0' }}>€{Number(draft.prezzo || 0).toFixed(2)}</p>
                <span style={{ display: 'inline-block', marginTop: 4, padding: '2px 8px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 600, fontFamily: T.fontBody, background: piattaforma === 'Vinted' ? 'rgba(74,222,128,0.15)' : 'rgba(34,211,238,0.15)', color: piattaforma === 'Vinted' ? '#4ade80' : '#22d3ee' }}>
                  {piattaforma}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Vinted Link Ready */}
        {showVintedLink && (
          <div style={{ textAlign: 'center', padding: 20, marginTop: 16, background: '#1c1640', border: '1px solid #2d1b69', borderRadius: 20 }}>
            <div style={{ fontSize: '2rem', marginBottom: 12 }}>🛍️</div>
            <p style={{ color: '#6b7280', fontSize: '0.88rem', marginBottom: 20, fontFamily: 'DM Sans' }}>
              I dati sono pronti. Clicca il bottone per aprire Vinted.
            </p>
            <a
              href={vintedReadyUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => { setShowVintedLink(false); setShowVintedInstructions(true); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 10,
                background: 'linear-gradient(135deg,#7c3aed,#4f46e5)',
                color: 'white', padding: '16px 32px', borderRadius: 9999,
                fontFamily: 'DM Sans', fontWeight: 700, fontSize: '1rem',
                textDecoration: 'none', boxShadow: '0 4px 20px rgba(124,58,237,0.4)',
              }}
            >
              🛍️ Apri Vinted e pubblica
            </a>
            <button onClick={() => setShowVintedLink(false)} style={{ display: 'block', margin: '16px auto 0', background: 'none', border: 'none', color: '#6b7280', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'DM Sans' }}>
              Annulla
            </button>
          </div>
        )}

        {/* Istruzioni Vinted dopo apertura tab */}
        {showVintedInstructions && (
          <div style={{ background: '#1c1640', border: '1px solid #2d1b69', borderRadius: 20, padding: 24, marginTop: 16 }}>
            <h4 style={{ fontFamily: T.fontDisplay, fontSize: '1.1rem', color: T.text, margin: '0 0 14px' }}>🚀 Vinted si è aperto!</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
              {[
                { icon: '✅', text: 'Vinted è aperto nel browser' },
                { icon: '✅', text: 'I dati sono pronti per l\'estensione' },
                { icon: '⏳', text: 'L\'estensione sta compilando i campi...' },
                { icon: '⏳', text: 'Controlla e clicca Pubblica su Vinted' },
              ].map((s, i) => (
                <div key={i} style={{ background: '#151230', borderRadius: 10, padding: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: '1rem' }}>{s.icon}</span>
                  <span style={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.text }}>{s.text}</span>
                </div>
              ))}
            </div>
            <button onClick={async () => {
              try { await api.put(`/bozze/${draft.id}`, { stato: 'pubblicata' }); } catch {}
              onClose();
              document.getElementById('success-toast-overlay')?.remove();
              const container = document.createElement('div');
              container.id = 'success-toast-overlay';
              container.style.cssText = 'position:fixed;inset:0;z-index:999999;display:flex;align-items:center;justify-content:center;background:rgba(13,11,30,0.7);';
              container.innerHTML = `
                <div style="background:#151230;border:1px solid #7c3aed;border-radius:24px;padding:40px 48px;text-align:center;box-shadow:0 0 60px rgba(124,58,237,0.5);font-family:DM Sans,sans-serif;min-width:340px;max-width:420px;position:relative;z-index:1000000;filter:none !important;backdrop-filter:none !important;">
                  <div style="font-size:3.5rem;margin-bottom:16px;">🎉</div>
                  <div style="font-family:Syne,sans-serif;font-size:1.5rem;font-weight:800;color:#e2e8f0;margin-bottom:10px;">Pubblicato con successo!</div>
                  <div style="font-size:0.9rem;color:#a78bfa;margin-bottom:6px;font-weight:600;">${draft.titolo || 'Il tuo annuncio'}</div>
                  <div style="font-size:0.82rem;color:#6b7280;margin-bottom:24px;">è ora visibile su Vinted 🛍️</div>
                  <div style="height:4px;border-radius:2px;background:linear-gradient(90deg,#7c3aed,#4f46e5,#a78bfa);animation:shrinkBar 3s linear forwards;"></div>
                </div>
                <style>@keyframes shrinkBar{from{width:100%}to{width:0%}}</style>
              `;
              document.body.appendChild(container);
              container.addEventListener('click', (e) => { if (e.target === container) container.remove(); });
              setTimeout(() => { if (container.parentNode) { container.style.opacity = '0'; container.style.transition = 'opacity 0.3s'; setTimeout(() => container.remove(), 300); } }, 3000);
              setTimeout(() => onDone(), 3000);
            }} className="btn-primary" style={{ width: '100%', padding: 14, borderRadius: 14, justifyContent: 'center', fontSize: '0.95rem' }}>
              ✓ Ho pubblicato
            </button>
          </div>
        )}

        {/* FOOTER — Bottoni */}
        <div style={{ display: 'flex', gap: 10, paddingTop: 20, marginTop: 16, borderTop: '1px solid #2d1b69' }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '12px 0', borderRadius: 14,
            background: 'transparent', border: '1px solid #2d1b69',
            color: T.muted, fontWeight: 600, fontSize: '0.9rem', fontFamily: T.fontBody, cursor: 'pointer',
          }}>
            Annulla
          </button>
          <button onClick={pubblica} disabled={publishing} style={{
            flex: 1, padding: '12px 0', borderRadius: 14,
            background: publishing ? '#5b21b6' : '#7c3aed', border: 'none',
            color: 'white', fontWeight: 700, fontSize: '0.9rem', fontFamily: T.fontBody,
            cursor: publishing ? 'not-allowed' : 'pointer', opacity: publishing ? 0.6 : 1,
          }}>
            {publishing ? 'Pubblicazione...' : `🚀 Pubblica su ${piattaforma}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── AccountModal ─────────────────────────────────────────────
function AccountModal({ accounts, onClose, toast, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [piattaforma, setPiattaforma] = useState('Vinted');
  const [nomeAccount, setNomeAccount] = useState('');
  const [username, setUsername] = useState('');
  const [note, setNote] = useState('');
  const [colore, setColore] = useState(ACCOUNT_COLORS[0]);
  const [saving, setSaving] = useState(false);

  const inputStyle = {
    width: '100%',
    background: T.elem,
    border: `1px solid ${T.border}`,
    borderRadius: 12,
    padding: '10px 14px',
    color: T.text,
    fontSize: '0.85rem',
    fontFamily: T.fontBody,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const aggiungiAccount = async () => {
    if (!nomeAccount.trim()) {
      toast.error('Inserisci un nome per l\'account');
      return;
    }
    setSaving(true);
    try {
      await api.post('/account-piattaforme', {
        piattaforma,
        nome_account: nomeAccount.trim(),
        username: username.trim(),
        note: note.trim(),
        colore,
      });
      toast.success('Account aggiunto!');
      setShowAdd(false);
      setNomeAccount('');
      setUsername('');
      setNote('');
      onRefresh();
    } catch {
      toast.error('Errore nell\'aggiunta dell\'account');
    } finally {
      setSaving(false);
    }
  };

  const eliminaAccount = async (id) => {
    try {
      await api.delete(`/account-piattaforme/${id}`);
      toast.success('Account eliminato');
      onRefresh();
    } catch {
      toast.error('Errore nell\'eliminazione');
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(13,11,30,0.85)',
      zIndex: 300,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 24,
        padding: 28,
        maxWidth: 460,
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: '1.1rem', margin: 0 }}>
            I miei Account
          </h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: T.muted, fontSize: '1.3rem', cursor: 'pointer',
          }}>
            ✕
          </button>
        </div>

        {/* Lista account */}
        {accounts.length === 0 ? (
          <p style={{ color: T.faint, fontSize: '0.82rem', fontFamily: T.fontBody, textAlign: 'center', padding: 20 }}>
            Nessun account configurato
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {accounts.map((a) => (
              <div key={a.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px',
                background: T.elem,
                border: `1px solid ${T.border}`,
                borderRadius: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 10, height: 10, borderRadius: '50%',
                    background: a.colore || T.violet,
                  }} />
                  <div>
                    <span style={{ color: T.text, fontSize: '0.85rem', fontWeight: 600, fontFamily: T.fontBody }}>
                      {a.nome_account}
                    </span>
                    <span className="badge-violet" style={{ fontSize: '0.6rem', marginLeft: 8 }}>
                      {a.piattaforma}
                    </span>
                    {a.username && (
                      <div style={{ color: T.muted, fontSize: '0.72rem', fontFamily: T.fontBody }}>
                        @{a.username}
                      </div>
                    )}
                  </div>
                </div>
                <button onClick={() => eliminaAccount(a.id)} style={{
                  background: 'none', border: 'none', color: T.red,
                  fontSize: '0.75rem', cursor: 'pointer', fontWeight: 600, fontFamily: T.fontBody,
                }}>
                  Elimina
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Form aggiunta */}
        {showAdd ? (
          <div style={{
            background: T.elem,
            border: `1px solid ${T.border}`,
            borderRadius: 16,
            padding: 16,
            display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            <h4 style={{ color: T.text, fontSize: '0.9rem', fontFamily: T.fontBody, margin: 0 }}>
              Nuovo Account
            </h4>

            <div>
              <span style={{ color: T.muted, fontSize: '0.72rem', fontFamily: T.fontBody }}>Piattaforma</span>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                {PIATTAFORME.map((p) => (
                  <button key={p} onClick={() => setPiattaforma(p)} style={{
                    flex: 1, padding: '8px 0', borderRadius: 10,
                    border: `1px solid ${piattaforma === p ? T.violetDark : T.border}`,
                    background: piattaforma === p ? T.violetDark : 'transparent',
                    color: piattaforma === p ? '#fff' : T.muted,
                    fontWeight: 600, fontSize: '0.82rem',
                    cursor: 'pointer', fontFamily: T.fontBody,
                  }}>
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <span style={{ color: T.muted, fontSize: '0.72rem', fontFamily: T.fontBody }}>Nome account</span>
              <input style={{ ...inputStyle, marginTop: 4 }} value={nomeAccount}
                onChange={(e) => setNomeAccount(e.target.value)} placeholder="Es. Il mio shop" />
            </div>

            <div>
              <span style={{ color: T.muted, fontSize: '0.72rem', fontFamily: T.fontBody }}>Username</span>
              <input style={{ ...inputStyle, marginTop: 4 }} value={username}
                onChange={(e) => setUsername(e.target.value)} placeholder="@username" />
            </div>

            <div>
              <span style={{ color: T.muted, fontSize: '0.72rem', fontFamily: T.fontBody }}>Note</span>
              <input style={{ ...inputStyle, marginTop: 4 }} value={note}
                onChange={(e) => setNote(e.target.value)} placeholder="Note opzionali..." />
            </div>

            {/* Color picker */}
            <div>
              <span style={{ color: T.muted, fontSize: '0.72rem', fontFamily: T.fontBody }}>Colore</span>
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                {ACCOUNT_COLORS.map((c) => (
                  <div key={c} onClick={() => setColore(c)} style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: c, cursor: 'pointer',
                    border: colore === c ? '3px solid #fff' : '3px solid transparent',
                    transition: 'border-color 0.2s',
                  }} />
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-ghost" onClick={() => setShowAdd(false)}
                style={{ flex: 1, fontSize: '0.8rem', fontFamily: T.fontBody }}>
                Annulla
              </button>
              <button className="btn-primary" onClick={aggiungiAccount} disabled={saving}
                style={{ flex: 1, fontSize: '0.8rem', fontFamily: T.fontBody }}>
                {saving ? 'Salvataggio...' : 'Aggiungi'}
              </button>
            </div>
          </div>
        ) : (
          <button className="btn-secondary" onClick={() => setShowAdd(true)}
            style={{ width: '100%', fontFamily: T.fontBody, fontSize: '0.85rem' }}>
            + Aggiungi Account
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Componenti Helper ────────────────────────────────────────
function MiniBtn({ label, onClick }) {
  return (
    <button onClick={onClick} style={{
      background: T.elem,
      border: `1px solid ${T.border}`,
      borderRadius: 8,
      padding: '5px 12px',
      color: T.text,
      fontSize: '0.72rem',
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: T.fontBody,
    }}>
      {label}
    </button>
  );
}

function SliderRow({ label, value, onChange, min = -100, max = 100 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ color: T.muted, fontSize: '0.7rem', fontFamily: T.fontBody, minWidth: 70 }}>{label}</span>
      <input type="range" min={min} max={max} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ flex: 1, accentColor: T.violetDark, height: 4 }} />
      <span style={{ color: T.faint, fontSize: '0.65rem', fontFamily: T.fontMono, minWidth: 30, textAlign: 'right' }}>
        {value}
      </span>
    </div>
  );
}
